import { useMemo, useState } from 'react';
import { Avatar } from './primitives/Avatar';
import { Button } from './primitives/Button';
import { InspectorTabs } from './primitives/InspectorTabs';
import { Select } from './primitives/Select';
import { Modal } from './primitives/Modal';
import { DiffView } from './primitives/DiffView';
import { Markdown } from './primitives/Markdown';
import {
  useVersionHistoryVM,
  type ActivityLine,
  type UseVersionHistoryVM
} from '../lib/vm/useVersionHistoryVM';
import type {
  AuditEventType,
  SubjectKind
} from '../lib/models/versionHistory';

export interface HistoryDrawerProps {
  subjectKind: SubjectKind;
  subjectId: string;
  open: boolean;
  onClose: () => void;
  /** The live title/name + body, for the Diff "current" side. */
  liveTitle: string;
  liveBody: string;
  /**
   * The VM. DocumentEditor / WorkItemDetail own the hook so the page can also
   * react to restore (refresh its own drafts). When omitted the drawer creates
   * its own — used by the standalone TestHistoryDrawer page.
   */
  vm?: UseVersionHistoryVM;
}

const TAB_VERSIONS = 'versions';
const TAB_ACTIVITY = 'activity';

function wordCount(body: string): number {
  const t = body.trim();
  return t === '' ? 0 : t.split(/\s+/).length;
}

/** "May 31 · 11:40 AM" — date + time, middot-separated for the timeline. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
  return `${date} · ${time}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
}

/** Day bucket label for the activity stream: Today / Yesterday / "May 30". */
function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const startOf = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const today = startOf(new Date());
  const that = startOf(d);
  const dayMs = 86_400_000;
  if (that === today) return 'Today';
  if (that === today - dayMs) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Maps an audit event type to a timeline dot color class. */
function dotKind(eventType: AuditEventType): string {
  if (eventType === 'body_changed' || eventType === 'title_changed') {
    return 'edit';
  }
  if (eventType.startsWith('comment_')) return 'comment';
  if (
    eventType === 'lifecycle_changed' ||
    eventType === 'status_changed' ||
    eventType === 'subtype_changed'
  ) {
    return 'status';
  }
  if (eventType === 'property_changed' || eventType === 'parent_changed') {
    return 'property';
  }
  return 'default';
}

/** Groups newest-first activity lines into consecutive day buckets. */
function groupByDay(
  lines: ActivityLine[]
): { label: string; lines: ActivityLine[] }[] {
  const groups: { label: string; lines: ActivityLine[] }[] = [];
  for (const line of lines) {
    const label = dayLabel(line.at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.lines.push(line);
    else groups.push({ label, lines: [line] });
  }
  return groups;
}

export function HistoryDrawer({
  subjectKind,
  subjectId,
  open,
  onClose,
  liveTitle,
  liveBody,
  vm: vmProp
}: HistoryDrawerProps) {
  // The drawer always has a VM. Pages pass theirs so they share restore state;
  // the standalone test page lets the drawer make its own. When `vmProp` is
  // supplied the self-owned VM is still constructed (hooks can't be conditional)
  // but is fed an undefined subjectId so its load effect early-returns — no
  // duplicate Supabase fetch.
  const ownVm = useVersionHistoryVM(
    subjectKind,
    vmProp ? undefined : subjectId
  );
  const vm = vmProp ?? ownVm;

  // `InspectorTabs` is controlled-only (no `defaultTab` prop), so the drawer
  // owns the active-tab state and feeds it back via `activeTab`/`onTabChange`.
  const [tab, setTab] = useState<string>(TAB_VERSIONS);

  // Per-snapshot word count + delta vs the next-older snapshot (snapshots are
  // newest-first). The oldest snapshot has no predecessor, so no delta.
  const words = useMemo(
    () => vm.snapshots.map((s) => wordCount(s.body)),
    [vm.snapshots]
  );

  const versionsPanel = (
    <div
      className="pn-history-drawer-panel"
      data-testid="history-drawer-versions"
      data-row-count={vm.snapshots.length}
    >
      {vm.status === 'loading' ? (
        <p className="pn-history-drawer-empty">Loading…</p>
      ) : vm.snapshots.length === 0 ? (
        <p className="pn-history-drawer-empty">No saved versions yet.</p>
      ) : (
        <ol className="pn-vtl">
          {/* one card per snapshot; the newest is flagged is-current */}
            {vm.snapshots.map((s, i) => {
              const isCurrent = i === 0;
              const delta = i < words.length - 1 ? words[i] - words[i + 1] : 0;
              return (
                <li
                  key={s.id}
                  className={`pn-vtl-item${isCurrent ? ' is-current' : ''}`}
                  data-testid={`history-drawer-version-${s.id}`}
                >
                  <span className="pn-vtl-node" aria-hidden="true" />
                  <div className="pn-vcard">
                    <div className="pn-vcard-top">
                      <span className="pn-vbadge">v{s.versionNumber}</span>
                      {isCurrent ? (
                        <span className="pn-vtag">Current</span>
                      ) : null}
                      <span className="pn-vtime">
                        {formatDateTime(s.snapshotAt)}
                      </span>
                    </div>
                    <div className="pn-vcard-meta">
                      <span className="pn-vwho">
                        <Avatar name={vm.authorFor(s)} />
                        <span className="pn-vwho-name">{vm.authorFor(s)}</span>
                      </span>
                      <span className="pn-vlen">
                        {words[i].toLocaleString()} words
                        {delta !== 0 ? (
                          <span
                            className={`pn-vdelta${delta < 0 ? ' is-neg' : ''}`}
                          >
                            {delta > 0 ? `+${delta}` : delta}
                          </span>
                        ) : null}
                      </span>
                    </div>
                    <div className="pn-vcard-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => vm.actions.openView(s.id)}
                        data-testid={`history-drawer-version-view-${s.id}`}
                      >
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => vm.actions.openDiff(s.id)}
                        data-testid={`history-drawer-version-diff-${s.id}`}
                      >
                        Diff vs current
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => vm.actions.openRestore(s.id)}
                        data-testid={`history-drawer-version-restore-${s.id}`}
                      >
                        Restore
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
        </ol>
      )}
    </div>
  );

  const activityGroups = groupByDay(vm.visibleActivityLines);

  const activityPanel = (
    <div
      className="pn-history-drawer-panel"
      data-testid="history-drawer-activity"
      data-row-count={vm.visibleActivityLines.length}
    >
      <div className="pn-atl-toolbar">
        <Select
          id="history-drawer-activity-filter"
          label="Filter by event type"
          hideLabel
          value={vm.activityFilter}
          onChange={(e) => vm.actions.setActivityFilter(e.target.value)}
          options={vm.activityFilterOptions}
          data-testid="history-drawer-activity-filter"
        />
      </div>
      {vm.status === 'loading' ? (
        <p className="pn-history-drawer-empty">Loading…</p>
      ) : vm.visibleActivityLines.length === 0 ? (
        <p className="pn-history-drawer-empty">
          No activity matches the current filter.
        </p>
      ) : (
        activityGroups.map((group, gi) => (
          <div key={`${group.label}-${gi}`}>
            <div className="pn-atl-day">
              <span className="pn-atl-day-label">{group.label}</span>
            </div>
            <ul className="pn-atl">
              {group.lines.map((line) => (
                <li
                  key={line.id}
                  className="pn-atl-event"
                  data-testid={`history-drawer-event-${line.id}`}
                  data-event-type={line.eventType}
                >
                  <span
                    className="pn-atl-dot"
                    data-kind={dotKind(line.eventType)}
                    aria-hidden="true"
                  />
                  <span className="pn-atl-text">{line.text}</span>
                  <span className="pn-atl-time">{formatTime(line.at)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );

  const viewModal = (
    <Modal
      open={vm.modal === 'view' && vm.modalSnapshot !== null}
      onClose={vm.actions.closeModal}
      title={
        vm.modalSnapshot
          ? `Version ${vm.modalSnapshot.versionNumber}`
          : 'Version'
      }
      size="wide"
      dataTestId="history-drawer-view-modal"
    >
      {vm.modalSnapshot ? (
        <Markdown dataTestId="history-drawer-view-body">
          {vm.modalSnapshot.body}
        </Markdown>
      ) : null}
    </Modal>
  );

  const diffModal = (
    <Modal
      open={vm.modal === 'diff' && vm.modalSnapshot !== null}
      onClose={vm.actions.closeModal}
      title={
        vm.modalSnapshot
          ? `Diff — version ${vm.modalSnapshot.versionNumber} vs current`
          : 'Diff'
      }
      size="wide"
      dataTestId="history-drawer-diff-modal"
    >
      {vm.modalSnapshot ? (
        <>
          {vm.modalSnapshot.title !== liveTitle ? (
            <p
              className="pn-history-title-change"
              data-testid="history-drawer-diff-title-change"
            >
              Title: "{vm.modalSnapshot.title}" → "{liveTitle}"
            </p>
          ) : null}
          <DiffView
            before={vm.modalSnapshot.body}
            after={liveBody}
            beforeLabel={`Version ${vm.modalSnapshot.versionNumber}`}
            afterLabel="Current"
            dataTestId="history-drawer-diff-view"
          />
        </>
      ) : null}
    </Modal>
  );

  const restoreModal = (
    <Modal
      open={vm.modal === 'restore' && vm.modalSnapshot !== null}
      onClose={vm.actions.closeModal}
      title="Restore this version?"
      dataTestId="history-drawer-restore-modal"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={vm.actions.closeModal}
            disabled={vm.isRestoring}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void vm.actions.confirmRestore()}
            disabled={vm.isRestoring}
            data-testid="history-drawer-restore-confirm"
          >
            {vm.isRestoring ? 'Restoring…' : 'Restore'}
          </Button>
        </>
      }
    >
      <p className="pn-history-drawer-restore-copy">
        This replaces the current content with version{' '}
        {vm.modalSnapshot?.versionNumber}. Restore is non-destructive — the
        current content is snapshotted first, so you can undo it.
      </p>
      {vm.restoreError ? (
        <p
          className="pn-history-drawer-restore-error"
          data-testid="history-drawer-restore-error"
        >
          {vm.restoreError}
        </p>
      ) : null}
    </Modal>
  );

  const subjectAttrs = useMemo(
    () => ({ 'data-subject-kind': subjectKind, 'data-subject-id': subjectId }),
    [subjectKind, subjectId]
  );

  const subjectNoun = subjectKind === 'document' ? 'Document' : 'Work item';
  const versionCount = vm.snapshots.length;
  const subtitle = `${subjectNoun} · ${versionCount} ${
    versionCount === 1 ? 'version' : 'versions'
  }`;

  return (
    <>
      <InspectorTabs
        open={open}
        onClose={onClose}
        kicker="Version history"
        title={liveTitle}
        subtitle={subtitle}
        activeTab={tab}
        onTabChange={setTab}
        ariaLabel="History"
        wide
        dataTestId="history-drawer"
        tabs={[
          {
            value: TAB_VERSIONS,
            label: (
              <>
                Versions{' '}
                <span className="pn-tab-count">{vm.snapshots.length}</span>
              </>
            ),
            panel: (
              <div className="pn-history-drawer" {...subjectAttrs}>
                {versionsPanel}
              </div>
            )
          },
          {
            value: TAB_ACTIVITY,
            label: (
              <>
                Activity{' '}
                <span className="pn-tab-count">{vm.activityLines.length}</span>
              </>
            ),
            panel: (
              <div className="pn-history-drawer" {...subjectAttrs}>
                {activityPanel}
              </div>
            )
          }
        ]}
      />
      {viewModal}
      {diffModal}
      {restoreModal}
    </>
  );
}
